import * as React from "react";
import { stringify } from "querystring";
// I'd like to use the individual types of the union here but then TS can't track them anymore?
function createUnasked() {
    return {
        _type: "unasked",
        loading: false,
        data: undefined,
        error: undefined
    };
}
function createSuccess(state) {
    return { _type: "success", ...state, loading: false, error: undefined };
}
function createLoading(state) {
    return {
        _type: "loading",
        ...state,
        loading: true,
        error: undefined
    };
}
function createFailure(e) {
    return {
        _type: "failure",
        loading: false,
        data: undefined,
        error: e
    };
}
export function matchFetchResult(m) {
    return function (s) {
        if (s._type === "unasked") {
            return m.unasked();
        }
        else if (s._type === "success") {
            return m.success(s);
        }
        else if (s._type === "loading") {
            return m.loading(s);
        }
        else if (s._type === "failure") {
            return m.failure(s);
        }
        else {
            throw Error("No match for FetchResult");
        }
    };
}
/** Poor replacement for a proper object hash */
function fetchArgHash(arg) {
    return typeof arg === "string" ||
        typeof arg === "number" ||
        typeof arg === "boolean" ||
        arg === undefined ||
        arg === null
        ? arg
        : stringify(arg);
}
function fetchArgsEqual(a, b) {
    return fetchArgHash(a) === fetchArgHash(b);
}
/**
 * Create a typed Fetcher component
 */
export function createFetcher(options) {
    const cache = new Map();
    const read = (arg) => {
        const key = fetchArgHash(arg);
        let result = cache.get(key);
        if (!result) {
            result = createUnasked();
            cache.set(key, result);
        }
        return result;
    };
    /**
     * Generic Fetcher component.
     *
     * Use `createFetcher<DataType>()` to create a fetcher for a specific data type instead.
     */
    class Fetcher extends React.Component {
        constructor() {
            super(...arguments);
            this._hasUnmounted = false;
            this.state = {
                fetchState: createUnasked()
            };
            this.fetch = (fetchArg) => {
                const cacheKey = fetchArgHash(fetchArg);
                const currentResult = read(fetchArg);
                if (currentResult._type === "loading" ||
                    currentResult._type === "success" ||
                    currentResult._type === "failure") {
                    return;
                }
                const loading = createLoading({
                    fetchArg,
                    data: this.state.data // Current data
                });
                cache.set(cacheKey, loading);
                this.setState({
                    fetchState: loading
                });
                options.fetch(fetchArg).then((result) => {
                    result.fold(e => {
                        const failure = createFailure(e);
                        cache.set(cacheKey, failure);
                        if (!this._hasUnmounted) {
                            this.setState({ fetchState: failure });
                        }
                    }, data => {
                        // We made it!
                        const success = createSuccess({ data });
                        cache.set(cacheKey, success);
                        if (!this._hasUnmounted) {
                            this.setState({ fetchState: success, data });
                        }
                    });
                }, 
                // We should never get here because usually errors are already caught and passed with Either
                e => {
                    const failure = createFailure(e);
                    cache.set(cacheKey, failure);
                    if (!this._hasUnmounted) {
                        this.setState({ fetchState: failure });
                    }
                });
            };
        }
        componentDidMount() {
            if (this.props.fetchOnMount || this.props.fetchOnMount === undefined) {
                this.fetch(this.props.fetchArg);
            }
        }
        componentDidUpdate(prevProps) {
            this.fetch(this.props.fetchArg);
        }
        componentWillUnmount() {
            this._hasUnmounted = true;
        }
        render() {
            const { fetchArg } = this.props;
            let result = read(fetchArg);
            return this.props.children(result);
        }
    }
    Fetcher.defaultProps = {
        fetchOnMount: true
    };
    return Fetcher;
}



// WEBPACK FOOTER //
// ./src/components/Fetcher.tsx